import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/use-toast';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Check, ChevronsUpDown, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import useUserStore from '@/store/userStore';
import useStore from '@/store/useStore';
import {
  fetchProfileTournamentsForUser,
  fetchProfileTeamsForUser,
  assignTournamentRole,
  assignTeamRole,
  removeTournamentRole,
  removeTeamRole,
} from '@/services/userAssignmentService';

// Types for user, tournament, team, and assignment
interface User {
  id: string;
  email: string;
  fullName?: string;
}

interface Assignment {
  id?: string;
  userId: string;
  entityId: string;
  role: 'owner' | 'editor' | 'viewer';
}

const ROLE_OPTIONS = [
  { value: 'owner', label: 'Owner' },
  { value: 'editor', label: 'Editor' },
  { value: 'viewer', label: 'Viewer' },
];

const AdminControllerPage: React.FC = () => {
  const { users = [], fetchUsers } = useUserStore();
  const {
    tournaments = [],
    teams = [],
    fetchTournaments,
    fetchTeams,
  } = useStore();

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [tab, setTab] = useState<'tournaments' | 'teams'>('tournaments');
  const [userTournaments, setUserTournaments] = useState<any[]>([]);
  const [userTeams, setUserTeams] = useState<any[]>([]);
  const [searchUser, setSearchUser] = useState('');
  const [userComboboxOpen, setUserComboboxOpen] = useState(false);

  // Fetch all data on mount
  useEffect(() => {
    fetchUsers && fetchUsers();
    fetchTournaments && fetchTournaments();
    fetchTeams && fetchTeams();
  }, [fetchUsers, fetchTournaments, fetchTeams]);

  // Fetch assignments when user changes
  useEffect(() => {
    if (selectedUser) {
      fetchProfileTournamentsForUser(selectedUser.id).then(setUserTournaments);
      fetchProfileTeamsForUser(selectedUser.id).then(setUserTeams);
    } else {
      setUserTournaments([]);
      setUserTeams([]);
    }
  }, [selectedUser]);

  // User selector with autocomplete
  const filteredUsers = users.filter(
    u => u.email.toLowerCase().includes(searchUser.toLowerCase()) || (u.fullName || '').toLowerCase().includes(searchUser.toLowerCase())
  );

  // Assignment handlers
  const handleTournamentRoleChange = async (tournamentId: string, role: string) => {
    if (!selectedUser) return;
    if (role === 'none') {
      await removeTournamentRole(selectedUser.id, tournamentId);
      fetchProfileTournamentsForUser(selectedUser.id).then(setUserTournaments);
      toast({ title: 'Success', description: 'Tournament role removed.' });
      return;
    }
    try {
      await assignTournamentRole(selectedUser.id, tournamentId, role);
      fetchProfileTournamentsForUser(selectedUser.id).then(setUserTournaments);
      toast({ title: 'Success', description: 'Tournament role updated.' });
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to update tournament role.', variant: 'destructive' });
    }
  };
  const handleTeamRoleChange = async (teamId: string, role: string) => {
    if (!selectedUser) return;
    if (role === 'none') {
      await removeTeamRole(selectedUser.id, teamId);
      fetchProfileTeamsForUser(selectedUser.id).then(setUserTeams);
      toast({ title: 'Success', description: 'Team role removed.' });
      return;
    }
    try {
      await assignTeamRole(selectedUser.id, teamId, role);
      fetchProfileTeamsForUser(selectedUser.id).then(setUserTeams);
      toast({ title: 'Success', description: 'Team role updated.' });
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to update team role.', variant: 'destructive' });
    }
  };

  return (
    <div className="container mx-auto py-8">
      <Card>
        <CardHeader>
          <CardTitle>Admin User Role Controller</CardTitle>
        </CardHeader>
        <CardContent>
          {/* User Autocomplete Selector */}
          <div className="mb-6">
            <label className="text-sm font-medium mb-2 block">Select User</label>
            <Popover open={userComboboxOpen} onOpenChange={setUserComboboxOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={userComboboxOpen}
                  className="w-full justify-between"
                >
                  {selectedUser ? (
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      <span className="font-medium">{selectedUser.fullName || selectedUser.email}</span>
                      <span className="text-muted-foreground text-sm">({selectedUser.email})</span>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">Select a user...</span>
                  )}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" align="start">
                <Command>
                  <CommandInput 
                    placeholder="Search users by email or name..." 
                    value={searchUser}
                    onValueChange={setSearchUser}
                  />
                  <CommandList>
                    <CommandEmpty>No users found.</CommandEmpty>
                    <CommandGroup>
                      {filteredUsers.map((user) => (
                        <CommandItem
                          key={user.id}
                          value={`${user.email} ${user.fullName || ''}`}
                          onSelect={() => {
                            setSelectedUser(user);
                            setUserComboboxOpen(false);
                            setSearchUser('');
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedUser?.id === user.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <div className="flex flex-col">
                            <span className="font-medium">{user.fullName || user.email}</span>
                            <span className="text-sm text-muted-foreground">{user.email}</span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            
            {selectedUser && (
              <div className="mt-2 p-3 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{selectedUser.fullName || 'No name provided'}</p>
                    <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedUser(null);
                      setUserTournaments([]);
                      setUserTeams([]);
                    }}
                  >
                    Clear
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Tabs for Tournaments/Teams */}
          <Tabs value={tab} onValueChange={v => setTab(v as any)}>
            <TabsList>
              <TabsTrigger value="tournaments">Tournaments</TabsTrigger>
              <TabsTrigger value="teams">Teams</TabsTrigger>
            </TabsList>
            <TabsContent value="tournaments">
              <div className="mt-4">
                <h3 className="font-semibold mb-2">Tournaments</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full border text-sm">
                    <thead>
                      <tr className="bg-muted">
                        <th className="p-2 text-left">Tournament</th>
                        <th className="p-2 text-left">Role</th>
                        <th className="p-2 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tournaments.map(tournament => {
                        const assignment = userTournaments.find(a => a.id === tournament.id);
                        return (
                          <tr key={tournament.id} className="border-b">
                            <td className="p-2">{tournament.name}</td>
                            <td className="p-2">
                              <Select
                                value={assignment?.role || 'none'}
                                onValueChange={role => handleTournamentRoleChange(tournament.id, role)}
                                disabled={!selectedUser}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue placeholder="Select role" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">None</SelectItem>
                                  {ROLE_OPTIONS.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="p-2">
                              {assignment && (
                                <Button size="sm" variant="destructive" onClick={() => handleTournamentRoleChange(tournament.id, 'none')}>
                                  Remove
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="teams">
              <div className="mt-4">
                <h3 className="font-semibold mb-2">Teams</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full border text-sm">
                    <thead>
                      <tr className="bg-muted">
                        <th className="p-2 text-left">Team</th>
                        <th className="p-2 text-left">Role</th>
                        <th className="p-2 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teams.map(team => {
                        const assignment = userTeams.find(a => a.id === team.id);
                        return (
                          <tr key={team.id} className="border-b">
                            <td className="p-2">{team.name}</td>
                            <td className="p-2">
                              <Select
                                value={assignment?.role || 'none'}
                                onValueChange={role => handleTeamRoleChange(team.id, role)}
                                disabled={!selectedUser}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue placeholder="Select role" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">None</SelectItem>
                                  {ROLE_OPTIONS.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="p-2">
                              {assignment && (
                                <Button size="sm" variant="destructive" onClick={() => handleTeamRoleChange(team.id, 'none')}>
                                  Remove
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminControllerPage; 